/**
 * Tipos internos da tela de cadastros-base.
 *
 * Papel: aqui ficam `ResidenciaNoFormulario` e `PessoaNoFormulario`, os
 * formatos que o react-hook-form guarda enquanto a pessoa preenche, e os dois
 * valores em branco de onde a criação parte.
 *
 * Os dois são derivados dos schemas do contrato com `z.input`, e não escritos à
 * mão, porque é a entrada daqueles schemas que o formulário precisa produzir.
 * Um tipo próprio compilaria e passaria a divergir no dia em que um campo
 * mudasse de forma no contrato, e a divergência apareceria como erro de
 * gravação, não de compilação.
 */
import { type novaPessoaFamiliaSchema, type novaResidenciaSchema } from '@concierge/shared';
import { type z } from 'zod';

/** A residência como o formulário a guarda, antes de o schema normalizar. */
export type ResidenciaNoFormulario = z.input<typeof novaResidenciaSchema>;

/** A pessoa da família como o formulário a guarda. */
export type PessoaNoFormulario = z.input<typeof novaPessoaFamiliaSchema>;

/**
 * Uma residência em branco, como o formulário de criação começa.
 *
 * `nome`, `apelido` e `cidade` entram como string vazia, e não ausentes: com
 * `undefined`, o `register` montaria um campo sem valor inicial, e o React
 * passaria a reclamar da troca de não controlado para controlado na primeira
 * tecla.
 *
 * `uf` entra vazia por outro motivo: ela é ligada por `Controller`, e o
 * `Select` mostra o texto de `SelectValue` enquanto o valor não estiver entre
 * as siglas. É esse vazio que faz a escolha da UF começar em branco em vez de
 * já vir com a primeira sigla da lista, que ninguém escolheu.
 */
export const RESIDENCIA_EM_BRANCO: ResidenciaNoFormulario = {
  nome: '',
  apelido: '',
  cidade: '',
  uf: '',
  dentroDeId: null,
};

/** Uma pessoa em branco, como o formulário de criação começa. */
export const PESSOA_EM_BRANCO: PessoaNoFormulario = {
  nomeCompleto: '',
  apelido: '',
};
